import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaSpinner,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaUniversity,
  FaFileAlt,
  FaRegFileAlt,
  FaArrowLeft,
  FaCalendar,
  FaShieldAlt,
  FaRocket,
  FaTrophy,
  FaAward,
  FaStar,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const RecentResults = () => {
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState("all");
  const [isMobile, setIsMobile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { setShowFooter } = useFooter();

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    fetchRecentResults();
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    // Check dark mode
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, []);

  const fetchRecentResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "/api/results/recent",
      );
      setRecentResults(response.data);
    } catch (error) {
      console.error("Fetch recent results error:", error);
      toast.error("Failed to load recent results");
    } finally {
      setLoading(false);
    }
  };

  const openResultLink = (url, name) => {
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Opening ${name}...`);
  };

  const getYearForGrouping = (name) => {
    const matches = name.match(/\b(20\d{2})\b/g);
    if (matches && matches.length > 0) {
      return Math.max(...matches.map((m) => parseInt(m)));
    }
    return null;
  };

  const getYearRange = (name) => {
    const matches = name.match(/\b(20\d{2})\b/g);
    if (matches && matches.length > 1) {
      const years = matches.map((m) => parseInt(m)).sort((a, b) => a - b);
      return `${years[0]}-${years[years.length - 1]}`;
    }
    if (matches && matches.length === 1) {
      return matches[0];
    }
    return null;
  };

  const getUniqueYears = () => {
    const yearsSet = new Set();
    recentResults.forEach((result) => {
      const year = getYearForGrouping(result.name);
      if (year) yearsSet.add(year);
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  };

  const uniqueYears = getUniqueYears();

  const filterByYear = (results, year) => {
    if (year === "all") return results;
    return results.filter((r) => getYearForGrouping(r.name) === year);
  };

  const regularResults = recentResults.filter(
    (r) => r.examType === "Regular" || r.examType === "Both",
  );
  const revaluationResults = recentResults.filter(
    (r) => r.examType === "Revaluation" || r.examType === "Both",
  );

  const filteredRegular = filterByYear(regularResults, selectedYear);
  const filteredRevaluation = filterByYear(revaluationResults, selectedYear);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 200px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <FaSpinner size={40} color="#8b5cf6" />
          </motion.div>
          <p style={{ marginTop: "1rem", color: getTextSecondary() }}>
            Loading available results...
          </p>
        </div>
      </div>
    );
  }

  const RegularColumn = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ flex: 1, minWidth: 0 }}
    >
      <motion.div
        variants={itemVariants}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "1.2rem",
          paddingBottom: "0.5rem",
          borderBottom: `2px solid #10b981`,
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            background: "rgba(16,185,129,0.1)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaCheckCircle color="#10b981" size={16} />
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: "1.2rem",
            fontWeight: "600",
            color: getTextColor(),
          }}
        >
          Regular Examinations
        </h2>
        <span
          style={{
            background: "linear-gradient(135deg, #10b981, #059669)",
            color: "white",
            fontSize: "0.7rem",
            padding: "0.15rem 0.6rem",
            borderRadius: "20px",
          }}
        >
          {filteredRegular.length}
        </span>
      </motion.div>

      {filteredRegular.length === 0 ? (
        <motion.div
          variants={itemVariants}
          style={{
            textAlign: "center",
            padding: "2rem",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
            color: getTextSecondary(),
          }}
        >
          <FaFileAlt size={30} color={getTextSecondary()} />
          <p style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
            No regular results available
          </p>
        </motion.div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {filteredRegular.map((result, index) => {
            const yearRange = getYearRange(result.name);
            const isMultiYear = yearRange && yearRange.includes("-");

            return (
              <motion.div
                key={result._id}
                variants={itemVariants}
                whileHover={{ x: 5, y: -2 }}
                onClick={() => openResultLink(result.url, result.name)}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: "1rem",
                  cursor: "pointer",
                  border: `1px solid ${getBorderColor()}`,
                  borderLeft: `4px solid #10b981`,
                  transition: "all 0.3s",
                  boxShadow: isDarkMode
                    ? "0 10px 30px -8px rgba(0,0,0,0.3)"
                    : "0 10px 30px -8px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(16,185,129,0.1)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#10b981",
                    }}
                  >
                    <FaCalendarAlt size={22} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        color: getTextColor(),
                      }}
                    >
                      {result.name}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginTop: "0.3rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {isMultiYear && yearRange && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "12px",
                            background: isDarkMode
                              ? "rgba(139,92,246,0.1)"
                              : "#f1f5f9",
                            color: getTextSecondary(),
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          <FaCalendar size={10} /> {yearRange}
                        </span>
                      )}
                      {result.description && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            color: getTextSecondary(),
                          }}
                        >
                          {result.description}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      color: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      background: "rgba(16,185,129,0.1)",
                      padding: "0.3rem 0.8rem",
                      borderRadius: "20px",
                    }}
                  >
                    <span>View</span>
                    <FaExternalLinkAlt size={10} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );

  const RevaluationColumn = () => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ flex: 1, minWidth: 0 }}
    >
      <motion.div
        variants={itemVariants}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "1.2rem",
          paddingBottom: "0.5rem",
          borderBottom: `2px solid #f59e0b`,
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            background: "rgba(245,158,11,0.1)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaClock color="#f59e0b" size={16} />
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: "1.2rem",
            fontWeight: "600",
            color: getTextColor(),
          }}
        >
          Revaluation Results
        </h2>
        <span
          style={{
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            color: "white",
            fontSize: "0.7rem",
            padding: "0.15rem 0.6rem",
            borderRadius: "20px",
          }}
        >
          {filteredRevaluation.length}
        </span>
      </motion.div>

      {filteredRevaluation.length === 0 ? (
        <motion.div
          variants={itemVariants}
          style={{
            textAlign: "center",
            padding: "2rem",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
            color: getTextSecondary(),
          }}
        >
          <FaRegFileAlt size={30} color={getTextSecondary()} />
          <p style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
            No revaluation results available
          </p>
        </motion.div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {filteredRevaluation.map((result, index) => {
            const yearRange = getYearRange(result.name);
            const isMultiYear = yearRange && yearRange.includes("-");

            return (
              <motion.div
                key={result._id}
                variants={itemVariants}
                whileHover={{ x: 5, y: -2 }}
                onClick={() => openResultLink(result.url, result.name)}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: "1rem",
                  cursor: "pointer",
                  border: `1px solid ${getBorderColor()}`,
                  borderLeft: `4px solid #f59e0b`,
                  transition: "all 0.3s",
                  boxShadow: isDarkMode
                    ? "0 10px 30px -8px rgba(0,0,0,0.3)"
                    : "0 10px 30px -8px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(245,158,11,0.1)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#f59e0b",
                    }}
                  >
                    <FaRegFileAlt size={22} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        color: getTextColor(),
                      }}
                    >
                      {result.name}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginTop: "0.3rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {isMultiYear && yearRange && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "12px",
                            background: isDarkMode
                              ? "rgba(139,92,246,0.1)"
                              : "#f1f5f9",
                            color: getTextSecondary(),
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          <FaCalendar size={10} /> {yearRange}
                        </span>
                      )}
                      {result.description && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            color: getTextSecondary(),
                          }}
                        >
                          {result.description}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      color: "#f59e0b",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      background: "rgba(245,158,11,0.1)",
                      padding: "0.3rem 0.8rem",
                      borderRadius: "20px",
                    }}
                  >
                    <span>View</span>
                    <FaExternalLinkAlt size={10} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{ marginBottom: "2rem" }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#8b5cf6",
              textDecoration: "none",
              marginBottom: "1rem",
              fontSize: "0.9rem",
              fontWeight: 500,
              padding: "0.5rem 1rem",
              borderRadius: "10px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)";
              e.target.style.transform = "translateX(-5px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)";
              e.target.style.transform = "translateX(0)";
            }}
          >
            <FaArrowLeft /> Back to Dashboard
          </Link>

          <div
            style={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
              borderRadius: "28px",
              padding: "2rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
              transition={{ duration: 4, repeat: Infinity }}
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background:
                  "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
                borderRadius: "50%",
              }}
            />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "0.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HiOutlineSparkles size={24} color="white" />
                </div>
                <h1
                  style={{
                    fontSize: isMobile ? "1.8rem" : "2rem",
                    fontWeight: "bold",
                    color: "white",
                    margin: 0,
                  }}
                >
                  Recent <span style={{ color: "#8b5cf6" }}>Results</span>
                </h1>
              </div>
              <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
                Select an exam period to view your results on the VTU portal
              </p>
            </div>
          </div>
        </motion.div>

        {/* Year Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            display: "flex",
            gap: "0.5rem",
            marginBottom: "2rem",
            flexWrap: "wrap",
          }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedYear("all")}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "30px",
              border: "none",
              background:
                selectedYear === "all"
                  ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                  : isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "#f1f5f9",
              color: selectedYear === "all" ? "white" : getTextSecondary(),
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: selectedYear === "all" ? "600" : "500",
              transition: "all 0.3s",
              boxShadow:
                selectedYear === "all"
                  ? "0 2px 8px rgba(139,92,246,0.3)"
                  : "none",
            }}
          >
            All Years
          </motion.button>
          {uniqueYears.map((year) => (
            <motion.button
              key={year}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedYear(year)}
              style={{
                padding: "0.5rem 1.2rem",
                borderRadius: "30px",
                border: "none",
                background:
                  selectedYear === year
                    ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                    : isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "#f1f5f9",
                color: selectedYear === year ? "white" : getTextSecondary(),
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: selectedYear === year ? "600" : "500",
                transition: "all 0.3s",
                boxShadow:
                  selectedYear === year
                    ? "0 2px 8px rgba(139,92,246,0.3)"
                    : "none",
              }}
            >
              {year}
            </motion.button>
          ))}
        </motion.div>

        {/* Two Column Layout - Responsive */}
        {isMobile ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
          >
            <RegularColumn />
            <RevaluationColumn />
          </div>
        ) : (
          <div style={{ display: "flex", gap: "2rem" }}>
            <RegularColumn />
            <RevaluationColumn />
          </div>
        )}

        {/* Empty State */}
        {recentResults.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: "center",
              padding: "4rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              marginTop: "1rem",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem auto",
              }}
            >
              <FaUniversity size={40} color="#8b5cf6" />
            </div>
            <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
              No Results Available
            </h3>
            <p style={{ color: getTextSecondary(), fontSize: "0.9rem" }}>
              Check back later for updates
            </p>
          </motion.div>
        )}

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "2rem",
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            background: isDarkMode
              ? "rgba(139,92,246,0.05)"
              : "rgba(139,92,246,0.03)",
            borderRadius: "40px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            You will be redirected to the official VTU results portal to view
            your results.
          </span>
          <FaRocket size={12} color="#8b5cf6" />
        </motion.div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default RecentResults;

